import { useAuth0 } from '@auth0/auth0-react';
import {
    Alert,
    Backdrop,
    Box,
    Button,
    CircularProgress,
    Fade,
    Modal,
    Paper,
    Step,
    StepLabel,
    Stepper,
    Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import { addVerification } from '../../redux/ProfileSlice';
import ProgressComplete from '../ProgressComplete';
import Degree from './Degree';
import LicenceInformation from './LicenceInformation';

const StyledLink = styled(Link)(({ theme }) => ({
    color: 'inherit',
    textDecoration: 'none',
    '&:hover': {
        color: 'inherit',
        textDecoration: 'none',
    },
}));
const CertifyButton = styled(Button)(({ theme }) => ({
    padding: '16px 36px',
    fontWeight: '400',
    fontFamily: "'Barlow', sans-serif !important",
    fontSize: '14px !important',
    textAlign: 'center !important',
    textTransform: 'uppercase !important',
    borderRadius: '5px !important',
    letterSpacing: '2px',
    color: theme.palette.primary.main,
    background: theme.palette.white.main,
    transition: 'background 200ms ease 0s !important',
    boxSizing: 'border-box',
    zIndex: 4,
    height: '60px',
    width: '200px',
    left: '0px',
    top: '0px',
    cursor: 'pointer',
    boxShadow: 'none',
}));

const ModalPaper = styled(Paper)(({ theme }) => ({
    position: 'absolute',
    // background: '-linear-gradient(left, grey 25%, white 75%)',
    // background: '-moz-linear-gradient(left, grey 25%, white 75%)',
    // background: '-webkit-linear-gradient(left, grey 25%, white 75%)',
    // background: '-0-linear-gradient(left, grey 25%, white 75%)',
    backgroundColor: '-linear-gradient(left, grey 25%, white 75%)',
    borderRadius: '20px',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    padding: '20px',
    minWidth: '250px',
    boxShadow: theme.shadows[5],
}));

const LightPaper = styled(Paper)(({ theme }) => ({
    backgroundColor: theme.palette.third.light,
    padding: '15px',
}));

const WarningTypography = styled(Typography)(({ theme }) => ({
    color: theme.palette.secondary.main,
}));

const steps = [
    'Teacher Number',
    'Degree',
    'Drivers Licence',
    'Medicare',
    'Complete',
];

const CertifySection = props => {
    const dispatch = useDispatch();

    const { getAccessTokenSilently } = useAuth0();
    const [page, setPage] = useState(0);
    const [state, setState] = useState({
        documentsRetrieved: false,
        loading: false,
        documents: [],
        error: false,
        verify: { status: 'Processing' },
    });

    function handleNextButton() {
        setPage(page + 1);
    }

    useEffect(async () => {
        if (props.getDocumentTypes === false) return;
        if (state.documentsRetrieved || state.error) return;

        setState({
            ...state,
            loading: true,
        });
        const token = await getAccessTokenSilently({
            scope: process.env.REACT_APP_scope,
        });
        const documents = await axios.get('/getDocumentTypes', {
            baseURL: process.env.REACT_APP_API_GATEWAY,
            url: `/getDocumentTypes`,
            timeout: 30000,
            headers: {
                authorization: `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
        });
        console.log('documents', JSON.stringify(documents));
        const types = documents.data.Message.Data;
        if (documents.status === 200) {
            if (!types.includes('teacherId')) {
                setPage(0);
            } else if (!types.includes('degree')) {
                setPage(1);
            } else if (!types.includes('licence')) {
                setPage(2);
            } else if (!types.includes('medicare')) {
                setPage(3);
            } else {
                setPage(4);
            }
            setState({
                ...state,
                documentsRetrieved: true,
                documents: types,
                error: false,
                loading: false,
            });
        } else {
            setState({
                documentsRetrieved: false,
                documents: [],
                error: true,
                loading: false,
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state.documentsRetrieved]);

    useEffect(() => {
        async function finalPage() {
            if (page === 4) {
                setState({
                    ...state,
                    verify: { status: 'Processing' },
                });
                //submitLicence
                const token = await getAccessTokenSilently({
                    scope: process.env.REACT_APP_scope,
                });
                await axios
                    .post(
                        '/uploadUserDocument',
                        { data: 'final', type: 'verifyTeacher' },
                        {
                            baseURL: process.env.REACT_APP_API_GATEWAY,
                            url: '/uploadUserDocument',
                            timeout: 30000,
                            headers: {
                                authorization: `Bearer ${token}`,
                                'Content-Type': 'application/json',
                            },
                        }
                    )
                    .then(dynamoData => {
                        if (
                            dispatch(
                                addVerification({
                                    verified:
                                        dynamoData.data.Message.Data.verified,
                                })
                            )
                        ) {
                            setState({
                                ...state,
                                verify: { status: 'Approved' },
                            });
                        } else {
                            setState({
                                ...state,
                                verify: { status: 'Failed' },
                            });
                        }
                    })
                    .catch(err => {
                        setState({
                            ...state,
                            verify: { status: 'Failed' },
                        });
                        console.log('error', err);
                    });
            }
        }
        finalPage();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page]);

    return (
        <>
            {props.fullVerification ? (
                <Box>
                    <Alert
                        severity="success"
                        sx={{ marginBottom: '30px' }}
                        action={
                            <StyledLink to="../teacher-availability">
                                <Button color="inherit" size="small">
                                    Add Availability
                                </Button>
                            </StyledLink>
                        }
                    >
                        Profile verified
                    </Alert>
                </Box>
            ) : props.verified === 'DocsUploaded' ? (
                <LightPaper>
                    <Alert severity="info" sx={{ marginBottom: '30px' }}>
                        Waiting on Profile Verification
                    </Alert>
                    <Typography variant="body">
                        This can take up to 24 hours, we will email you when
                        your profile is verified and ready to use.
                    </Typography>
                </LightPaper>
            ) : props.skillsComplete ? (
                <Box display="flex" flexDirection="column" alignItems="center">
                    <Stepper activeStep={page} alternativeLabel>
                        {steps.map(label => (
                            <Step key={label}>
                                <StepLabel>{label}</StepLabel>
                            </Step>
                        ))}
                    </Stepper>
                    {state.loading ? (
                        <Box sx={{ textAlign: 'center', m: 2 }}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <Box sx={{ maxWidth: 400 }}>
                            {page === 0 && state.error === false && (
                                <LicenceInformation
                                    clickNextButton={handleNextButton}
                                    title="Teacher Licence Information"
                                    type="teacherId"
                                    name="Teacher"
                                    stringLength={1}
                                    nextButton={false}
                                    ext={false}
                                />
                            )}
                            {page === 1 && state.error === false && (
                                <Degree clickNextButton={handleNextButton} />
                            )}
                            {page === 2 && state.error === false && (
                                <LicenceInformation
                                    clickNextButton={handleNextButton}
                                    title="Driver Licence Information"
                                    type="licence"
                                    name="Licence"
                                    stringLength={6}
                                    nextButton={true}
                                    ext={false}
                                />
                            )}
                            {page === 3 && state.error === false && (
                                <LicenceInformation
                                    clickNextButton={handleNextButton}
                                    title="Medicare Information"
                                    type="medicare"
                                    name="Medicare"
                                    stringLength={10}
                                    nextButton={false}
                                    ext={true}
                                />
                            )}
                            {page === 4 && state.error === false && (
                                <Box
                                    sx={{
                                        padding: 1,
                                        display: 'flex',
                                        justifyContent: 'center',
                                    }}
                                >
                                    <ProgressComplete
                                        loading={
                                            state.verify.status === 'Processing'
                                                ? true
                                                : false
                                        }
                                        success={
                                            state.verify.status === 'Approved'
                                                ? true
                                                : false
                                        }
                                        failed={
                                            state.verify.status === 'Failed'
                                                ? true
                                                : false
                                        }
                                        successMessage="Profile Upload Complete"
                                        failedMessage="Profile Upload Complete"
                                    />
                                </Box>
                            )}
                            {state.error && (
                                <WarningTypography>
                                    Sorry, something went wrong
                                </WarningTypography>
                            )}
                        </Box>
                    )}
                </Box>
            ) : (
                <Alert
                    severity="warning"
                    action={
                        <StyledLink to="/profile/skills">
                            <Button color="inherit" size="small">
                                Skills page
                            </Button>
                        </StyledLink>
                    }
                >
                    Fill in your skills first!
                </Alert>
            )}
        </>
    );
};

export default CertifySection;
